import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, CreditCard, ExternalLink, Plug } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { ActionButton, CustomerPanel, StatusBadge } from "../components/customer/CustomerUI";
import { LoadingState, ErrorState } from "../components/LoadingState";
import api, { formatApiError } from "../lib/api";
import { PAID_PLANS, PLAN_OPERATIONAL_NOTE } from "../config/planCatalog";

const PLANS = PAID_PLANS;
const VALID_PLANS = new Set(PLANS.map((plan) => plan.id));

export function DowngradeImpact({ preview, periodEnd, onContinue, busy = false }) {
  if (!preview) return null;
  const removed = preview.removed_members || [];
  const cancelled = preview.cancelled_invitations || [];
  const hasImpact = removed.length > 0 || cancelled.length > 0;
  return <CustomerPanel
    data-testid="downgrade-seat-preview"
    title={`Before your plan changes: ${preview.seat_limit} CleanIQ seat${preview.seat_limit === 1 ? "" : "s"}`}
    description={periodEnd
      ? `This is the team-access change that will take effect on ${periodEnd}. A full refund would apply it immediately.`
      : "This team-access change applies immediately after a full refund, or at the end of a cancellation period."}
    className="mb-7"
    id="downgrade-seat-impact"
  >
    <div className="space-y-4 p-4 sm:p-5">
      {!hasImpact && <p className="text-sm text-slate-600">No memberships or pending invitations would be affected.</p>}
      {removed.length > 0 && <div>
        <div className="mb-2 flex items-center gap-2">
          <StatusBadge tone="danger">Moved to personal workspace</StatusBadge>
          <span className="text-sm text-slate-500">{removed.length} member{removed.length === 1 ? "" : "s"}</span>
        </div>
        <ul className="space-y-1 text-sm text-slate-700">
          {removed.map((member) => <li key={member.user_id} className="break-words">
            <span className="font-semibold">{member.name}</span>{member.email ? ` — ${member.email}` : ""}
          </li>)}
        </ul>
      </div>}
      {cancelled.length > 0 && <div>
        <div className="mb-2 flex items-center gap-2">
          <StatusBadge tone="warning">Invitations cancelled</StatusBadge>
          <span className="text-sm text-slate-500">{cancelled.length} invitation{cancelled.length === 1 ? "" : "s"}</span>
        </div>
        <ul className="space-y-1 text-sm text-slate-700">
          {cancelled.map((invitation) => <li key={invitation.invitation_id} className="break-all">{invitation.email}</li>)}
        </ul>
      </div>}
      {onContinue && <ActionButton
        id="continue-to-stripe-billing"
        onClick={onContinue}
        disabled={busy}
      >
        <ExternalLink aria-hidden="true" />
        {busy ? "Opening Stripe billing…" : "Continue to Stripe billing"}
      </ActionButton>}
    </div>
  </CustomerPanel>;
}

export default function Billing() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [downgradePreview, setDowngradePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const checkoutResult = searchParams.get("checkout");
  const selectedPlan = VALID_PLANS.has(searchParams.get("plan")) ? searchParams.get("plan") : null;
  const selectedPlanName = PLANS.find((plan) => plan.id === selectedPlan)?.name;

  const load = async () => {
    setLoading(true);
    try {
      const [subscription, status, preview] = await Promise.all([
        api.get("/billing/subscription"),
        api.get("/billing/stripe/status"),
        api.get("/billing/downgrade-preview").catch((error) => {
          if (error.response?.status === 403) return { data: null };
          throw error;
        }),
      ]);
      setData(subscription.data);
      setStripeStatus(status.data);
      setDowngradePreview(preview.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCheckout = async (plan) => {
    setBusy(plan);
    try {
      const { data: session } = await api.post("/billing/stripe/checkout", { plan });
      window.location.assign(session.url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy("");
    }
  };
  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data: session } = await api.post("/billing/stripe/portal");
      window.location.assign(session.url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy("");
    }
  };
  const reviewBillingImpact = () => {
    if (data?.effective_plan !== "free" && downgradePreview) {
      document.getElementById("downgrade-seat-impact")?.scrollIntoView({
        behavior: "smooth", block: "center",
      });
      document.getElementById("continue-to-stripe-billing")?.focus();
      return;
    }
    openPortal();
  };

  if (loading) return <AppShell><LoadingState label="Loading billing" /></AppShell>;
  if (!data || !stripeStatus) return <AppShell><ErrorState message="Billing information is unavailable." onRetry={load} /></AppShell>;
  const subscription = data.subscription;
  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    })
    : null;
  const refundDowngrade = subscription?.status === "refunded" || !!subscription?.refunded_at;
  const scheduledCancellation = subscription?.status === "active"
    && subscription?.cancel_at_period_end && periodEnd;
  const hasActiveSubscription = !!subscription?.provider_customer_id
    && !["canceled", "refunded", "unpaid", "incomplete_expired"].includes(subscription?.status);
  const canRetry = checkoutResult === "canceled" && selectedPlan && stripeStatus.checkout
    && !hasActiveSubscription;

  return <AppShell>
    {checkoutResult === "success" && <section data-testid="checkout-success"
      className="mb-6 flex flex-col gap-5 rounded-3xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-6">
      <div className="flex gap-4">
        <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
        <div>
          <h2 className="font-heading text-lg font-extrabold text-emerald-950">Payment received</h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-emerald-800">
            Your CleanIQ access is being activated. Stripe confirmation can take a moment; you can continue onboarding now.
          </p>
        </div>
      </div>
      <Link to="/onboarding"
        className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-emerald-700 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-900/10 hover:bg-emerald-800">
        <Plug className="h-4 w-4" /> Continue account setup
      </Link>
    </section>}

    {checkoutResult === "canceled" && <section data-testid="checkout-canceled"
      className="mb-6 flex flex-col gap-5 rounded-3xl border border-amber-200 bg-amber-50 p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-6">
      <div className="flex gap-4">
        <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />
        <div>
          <h2 className="font-heading text-lg font-extrabold text-amber-950">Checkout canceled</h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-amber-800">
            No charge was made. {selectedPlanName ? `Your ${selectedPlanName} selection is saved so you can try again.` : "Choose a plan below whenever you are ready."}
          </p>
        </div>
      </div>
      {canRetry && <button onClick={() => openCheckout(selectedPlan)} disabled={!!busy}
        className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-amber-700 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-amber-900/10 hover:bg-amber-800 disabled:opacity-50">
        <CreditCard className="h-4 w-4" /> {busy ? "Opening checkout…" : `Retry ${selectedPlanName}`}
      </button>}
    </section>}

    <div className="relative mb-8 flex flex-wrap items-end justify-between gap-4 overflow-hidden rounded-3xl bg-gradient-to-br from-[#081426] via-[#0b1d38] to-blue-700 p-6 text-white shadow-2xl shadow-blue-950/15 sm:p-8 lg:p-10">
      <div>
        <div className="mb-3 inline-flex rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-200">Plans & Billing</div>
        <h1 className="mt-1 max-w-3xl font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">Choose the right CleanIQ plan</h1>
        <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">Scans stay unlimited. Contact ceilings and operational features follow your plan.</p>
      </div>
      {subscription?.provider_customer_id && <button onClick={reviewBillingImpact} disabled={busy === "portal"}
        className="flex items-center gap-2 border border-border bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
        <ExternalLink className="h-4 w-4" /> Manage Billing
      </button>}
    </div>

    <div className="mb-7 rounded-2xl border border-slate-200 bg-white p-5 text-sm shadow-sm">
      <span className="font-mono text-xs uppercase tracking-wider text-slate-500">Current access</span>
      <div className="mt-1 font-semibold capitalize text-slate-900">{data.effective_plan}</div>
      {subscription && <div className="mt-1 text-xs text-slate-500">Subscription status: {subscription.status}</div>}
      {scheduledCancellation && <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900">
        Cancellation scheduled for {periodEnd}. Because no refund was issued, paid access continues through the period you already purchased.
      </div>}
      {refundDowngrade && <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-900">
        Full refund completed. This account was downgraded to Free immediately.
      </div>}
      {!stripeStatus.checkout && <div className="mt-3 border border-amber-200 bg-amber-50 p-3 text-amber-800">
        Online checkout is being configured. Internal access remains available.
      </div>}
    </div>

    {data.effective_plan !== "free" && <DowngradeImpact
      preview={downgradePreview}
      periodEnd={scheduledCancellation ? periodEnd : null}
      onContinue={openPortal}
      busy={busy === "portal"}
    />}

    <section className="mb-7 grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700 sm:grid-cols-2">
      <div>
        <div className="font-bold text-slate-950">Cancel without a refund</div>
        <p className="mt-1 leading-6">Turn off renewal and keep paid features until the end of the current billing period.</p>
      </div>
      <div>
        <div className="font-bold text-slate-950">Request a full refund</div>
        <p className="mt-1 leading-6">Approved full refunds immediately downgrade the account to Free.</p>
      </div>
    </section>
    <p className="mb-7 text-xs leading-6 text-slate-500">Plan purchases and account changes are governed by the <Link to="/terms" className="font-semibold underline">Terms of Service</Link> and <Link to="/refunds" className="font-semibold underline">Refund and Cancellation Policy</Link>.</p>

    <div className="grid min-w-0 gap-5 lg:grid-cols-3">
      {PLANS.map((plan) => <section key={plan.id}
        className={"relative min-w-0 overflow-hidden rounded-3xl border bg-white p-6 shadow-lg shadow-slate-900/5 transition hover:-translate-y-1 hover:shadow-xl sm:p-7 " + (plan.popular ? "border-blue-500 ring-2 ring-blue-500/10" : "border-slate-200")}>
        {plan.popular && <div className="absolute right-4 top-4 rounded-full bg-blue-600 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">Most popular</div>}
        <h2 className="font-heading text-xl font-extrabold text-slate-950">{plan.name}</h2>
        <div className="mt-6"><span className="font-mono text-5xl font-extrabold tracking-tight text-slate-950">{plan.price}</span><span className="text-sm text-slate-500">/month</span></div>
        <ul className="mt-6 space-y-3 border-t border-slate-100 pt-6 text-sm text-slate-600">
          <li>Up to {plan.contacts} active contacts</li>
          <li>{plan.crmConnections === 1 ? "1 CRM connection" : `Up to ${plan.crmConnections} CRM connections`}</li>
          <li>{plan.seats === 1 ? "1 CleanIQ user" : `Up to ${plan.seats} CleanIQ users`}</li>
          <li>Unlimited CRM cleaning jobs*</li>
          <li>Approval-gated CRM write-back</li>
        </ul>
        <button data-testid={"checkout-" + plan.id}
          disabled={!stripeStatus.checkout || hasActiveSubscription || !!busy}
          onClick={() => openCheckout(plan.id)}
          className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-blue-600/20 transition hover:brightness-110 disabled:cursor-not-allowed disabled:from-slate-300 disabled:to-slate-300 disabled:shadow-none">
          <CreditCard className="h-4 w-4" /> {busy === plan.id ? "Opening checkout…" : "Choose " + plan.name}
        </button>
      </section>)}
    </div>
    <p className="mt-4 text-xs leading-5 text-slate-500">* {PLAN_OPERATIONAL_NOTE}</p>
    <p className="mt-4 text-xs leading-5 text-slate-500">By starting checkout, you agree to the <Link to="/terms" className="font-semibold underline">Terms</Link>, <Link to="/privacy" className="font-semibold underline">Privacy Notice</Link>, and <Link to="/refunds" className="font-semibold underline">Refund and Cancellation Policy</Link>.</p>
  </AppShell>;
}
